import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const LINKS = [
  { to: "/", label: "Beranda" },
  { to: "/tentang", label: "Tentang" },
  { to: "/layanan", label: "Layanan" },
  { to: "/coloring", label: "Coloring" },
  { to: "/galeri", label: "Galeri" },
  { to: "/academy", label: "Academy" },
  { to: "/kontak", label: "Kontak" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { waLink } = useSettings();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const light = !scrolled && location.pathname === "/";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "glass-cream border-b border-brand-gold/25 shadow-[0_8px_32px_rgba(74,14,23,0.08)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 sm:h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
          <span className={`font-cinzel text-lg sm:text-xl font-bold tracking-[0.18em] transition-colors duration-500 ${light ? "text-brand-cream" : "text-brand-burgundy"}`}>
            SALON <span className="text-brand-gold">STRAWBERRY</span>
          </span>
          <span className={`mt-1 text-[9px] uppercase tracking-[0.25em] transition-colors duration-500 ${light ? "text-brand-cream/60" : "text-brand-ink-muted"}`}>Beauty Care, from Head to Toe</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop-menu">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] uppercase tracking-[0.15em] transition-colors duration-300 hover:text-brand-gold ${
                  isActive
                    ? light ? "text-brand-gold font-semibold" : "text-brand-burgundy font-semibold"
                    : light ? "text-brand-cream/75" : "text-brand-ink-muted"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-whatsapp-cta"
            href={waLink("Halo Salon Strawberry Official! Saya ingin booking appointment.")}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-burgundy px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cream transition-all duration-300 hover:bg-brand-burgundy-rich hover:shadow-[0_0_25px_rgba(212,175,55,0.3)]"
          >
            <MessageCircle size={14} />
            Booking
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 transition-colors duration-500 ${light ? "text-brand-cream" : "text-brand-burgundy"}`}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden glass-cream border-t border-brand-gold/25 px-6 py-6">
          <div className="flex flex-col gap-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.2em] ${isActive ? "text-brand-burgundy font-semibold" : "text-brand-ink-muted"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              data-testid="nav-mobile-whatsapp-cta"
              href={waLink("Halo Salon Strawberry Official! Saya ingin booking appointment.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-brand-burgundy px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cream"
            >
              <MessageCircle size={14} />
              Booking via WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
